import * as Sentry from "@sentry/react";

export type RetryOptions = {
  maxAttempts?: number;
  baseDelayMs?: number;
  isRetryable?: (error: unknown) => boolean;
  context?: string;
};

const isNetworkOrTimeoutError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as {
    code?: string;
    name?: string;
    response?: { status?: number };
  };
  // axios — connection aborted or network down
  if (err.code === "ECONNABORTED" || err.code === "ERR_NETWORK") return true;
  // axios — server-side errors are typically retryable
  const status = err.response?.status;
  if (status && status >= 500) return true;
  // fetch — AbortController.abort() in fetchWithTimeout
  if (err.name === "AbortError") return true;
  // fetch — TypeError thrown on network failure (DNS, offline, TLS, etc.)
  if (error instanceof TypeError) return true;
  return false;
};

/**
 * Runs `fetch` with a hard timeout via AbortController. Throws an AbortError
 * (which `withRetry` treats as retryable) if `timeoutMs` elapses before the
 * response headers arrive.
 */
export const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init: RequestInit & { timeoutMs: number },
): Promise<Response> => {
  const { timeoutMs, ...rest } = init;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

export const withRetry = async <T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> => {
  const maxAttempts = opts.maxAttempts ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 1000;
  const isRetryable = opts.isRetryable ?? isNetworkOrTimeoutError;
  const context = opts.context ?? "withRetry";

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await fn();
      if (attempt > 1) {
        try {
          Sentry.addBreadcrumb({
            category: "auth.retry",
            message: `${context} succeeded on attempt ${attempt}`,
            level: "info",
          });
        } catch {
          // Sentry must never block the auth flow.
        }
      }
      return result;
    } catch (error) {
      lastError = error;
      const retryable = isRetryable(error);
      try {
        Sentry.addBreadcrumb({
          category: "auth.retry",
          message: `${context} attempt ${attempt} failed`,
          data: {
            attempt,
            retryable,
            message: (error as Error)?.message,
          },
          level: "warning",
        });
      } catch {
        // Sentry must never block the auth flow.
      }
      if (!retryable || attempt === maxAttempts) throw error;
      const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  throw lastError;
};
